<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import AuthLayout from '../components/AuthLayout.vue';
import { Button, Input } from '../components/ui';
import { requestAccess } from '../api';
import { signInPath } from '../services/authPaths';
import { loadRecaptchaScript, executeRecaptcha } from '../services/recaptcha';
import { isValidEmail } from '../services/validators';

const firstName = ref('');
const lastName = ref('');
const email = ref('');
const firstNameError = ref('');
const lastNameError = ref('');
const emailError = ref('');
const loading = ref(false);
const success = ref(false);

const topLinks = computed(() => {
  if (success.value) {
    return [
      { url: '/', text: 'Back Home' },
      { url: '/about', text: 'About Us' },
      { url: '/past_events', text: 'Notable Past Events' },
    ];
  }
  return [{ url: signInPath(), text: 'Sign in' }];
});

const bottomLinks = computed(() => []);

async function handleSubmit() {
  firstNameError.value = '';
  lastNameError.value = '';
  emailError.value = '';

  let hasError = false;

  if (!firstName.value.trim()) {
    firstNameError.value = 'First name can not be blank.';
    hasError = true;
  }

  if (!lastName.value.trim()) {
    lastNameError.value = 'Last name can not be blank.';
    hasError = true;
  }

  if (!email.value.trim()) {
    emailError.value = 'Email can not be blank.';
    hasError = true;
  } else if (!isValidEmail(email.value)) {
    emailError.value = 'Enter a valid email.';
    hasError = true;
  }

  if (hasError) return;

  loading.value = true;

  try {
    const token = await executeRecaptcha('PlatformAccessRequest');
    if (!token) {
      emailError.value = 'Security verification failed. Please refresh and try again.';
      return;
    }

    await requestAccess(firstName.value, lastName.value, email.value, token);
    success.value = true;
  } catch {
    emailError.value = 'Unable to submit access request. Please try again.';
  } finally {
    loading.value = false;
  }
}

onMounted(async () => {
  try {
    await loadRecaptchaScript();
  } catch (error) {
    console.error('reCAPTCHA failed to load:', error);
  }
});
</script>

<template>
  <AuthLayout title="Welcome" :top-links="topLinks" :bottom-links="bottomLinks">
    <div v-if="success" id="spire-user-auth-success" class="text-white text-left">
      <h2 class="text-2xl font-bold mb-4">We'll follow up</h2>
      <p class="text-white/80">
        Thanks for your interest! A member of our team will reach back out if your request has been accepted.
      </p>
    </div>

    <form v-else @submit.prevent="handleSubmit">
      <Input
        v-model="firstName"
        type="text"
        placeholder="First Name"
        autocomplete="given-name"
        :error="firstNameError"
      />

      <Input v-model="lastName" type="text" placeholder="Last Name" autocomplete="family-name" :error="lastNameError" />

      <Input v-model="email" type="email" placeholder="Email" autocomplete="email" :error="emailError" />

      <div class="mt-form-gap">
        <Button type="submit" variant="primary" size="lg" :loading="loading" :disabled="loading"> Send Request </Button>
      </div>
    </form>
  </AuthLayout>
</template>
